import React, { useRef, useEffect, useState } from 'react';
import { BackButton, DescribeContent, DescribeFormButtons, DescribeFormContainer, DescribeFormContent, NextButton } from './index.style';
import LeadForm from '../../lead-form/LeadForm';
import LeadFormItem from '../../lead-form/LeadFormItem';
import { QaItem } from '../../../types';

interface DescribeFormData {
    jobDescription?: string;
    postcode?: string;
    qaList?: string;
    remote?: string;
}

interface Props {
    subDescription?: string;
    onBack: () => void;
    onNext: () => void;
    formData: DescribeFormData;
    onFormDataChange: (name: string, value: string) => void;
    qaList: QaItem[];
    nationalWide: string;
    app?: boolean;
}

const EMAIL_PATTERN = /@[^@]*\.(com|org|net|info|biz|io|edu|gov|tech)\b/i;
const URL_PATTERNS = [
    /https?:\/\/[^\s]+/i,
    /www\.[^\s]+/i,
    /\.(com|org|net|info|biz|io|edu|gov|tech)\b/i,
];

const checkTenDigits = (text: string, errorMessage: string): boolean | string => {
    let count = 0;
    const validSeparators = /[\s~!@#$%^&*()`\-=_+[\]\\{}|;':",./<>?]/;
    
    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        
        if (/\d/.test(char)) {
            if (++count >= 10) {
                return errorMessage;
            }
        } else if (!validSeparators.test(char)) {
            count = 0;
        }
    }
    
    return true;
};

const validateJobDescription = (value: string | number): boolean | string => {
    const text = String(value);
    const errorMessage = "Your current input may contain a phone number, email address, or URL. Please modify it before submitting again.";
    
    const allPatterns = [
        EMAIL_PATTERN,
        ...URL_PATTERNS
    ];
    
    if (allPatterns.some(pattern => pattern.test(text))) {
        return errorMessage;
    }
    
    return checkTenDigits(text, errorMessage);
};

const DescribeForm: React.FC<Props> = ({
    subDescription,
    onBack,
    onNext,
    formData,
    onFormDataChange,
    qaList,
    nationalWide,
    app = false,
}) => {
    const formRef = useRef<any>();
    const [showButtons, setShowButtons] = useState(true);
    const segmentedOptions = [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" }
    ];

    const shouldShowRemote = nationalWide === 'national' || nationalWide === 'remote';

    useEffect(() => {
        if (!app) return;
        const handleResize = () => {
            const isKeyboardOpen = window.visualViewport && window.visualViewport.height < 500;
            setShowButtons(!isKeyboardOpen);
        };
        window.visualViewport?.addEventListener('resize', handleResize);
        return () => window.visualViewport?.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (shouldShowRemote && nationalWide === 'national' && !formData.remote) {
            onFormDataChange('remote', 'yes');
        }
    }, [nationalWide, shouldShowRemote, formData.remote, onFormDataChange]);

    const selectOptions = qaList && qaList.length > 0 
        ? qaList[0].answers.map(answer => ({
            label: answer.answerText,
            value: answer.answerId
        }))
        : [];

    const handleNextClick = () => {
        if (formRef.current && formRef.current.validate()) {
            onNext();
        }
    };

    return (
        <DescribeFormContainer>
            <DescribeFormContent>
                <h2>Describe your job</h2>
                <DescribeContent>
                    {subDescription}
                </DescribeContent>
                <LeadForm ref={formRef}>
                    <LeadFormItem
                        type="textarea"
                        label="Job description"
                        name="jobDescription"
                        required
                        validator={validateJobDescription}
                        errorMessage={{
                            required: "Please enter a job description",
                            minLength: "You must write at least 15 words in your description",
                        }}
                        maxLength={10000}
                        minLength={15}
                        value={formData.jobDescription || ''}
                        onChange={(value) => onFormDataChange('jobDescription', value as string)}
                    />
                    {shouldShowRemote && (
                        <LeadFormItem
                            type="segmented"
                            label="Would you be open to receiving this service remotely?"
                            name="remote"
                            required
                            options={segmentedOptions}
                            errorMessage="Please answer to continue"
                            value={formData.remote || ''}
                            onChange={(value) => onFormDataChange('remote', value as string)}
                        />
                    )}
                    <LeadFormItem
                        type="input"
                        label="Postcode"
                        name="postcode"
                        required
                        pattern={/^[A-Z]{1,2}[0-9]{1,2}\s?[0-9][A-Z]{2}$/i}
                        errorMessage={{
                            required: "Please enter your postcode",
                            pattern: "Please enter the valid postcode",
                        }}
                        value={formData.postcode || ''}
                        onChange={(value) => onFormDataChange('postcode', value as string)}
                    />
                    <LeadFormItem
                        type="select"
                        label="When would you like the job to start?"
                        name="qaList"
                        required
                        options={selectOptions}
                        errorMessage="Please select an option"
                        value={formData.qaList || ''}
                        onChange={(value) => onFormDataChange('qaList', value as string)}
                    />
                </LeadForm>
            </DescribeFormContent>
            {showButtons && (
                <DescribeFormButtons>
                    <BackButton type="button" onClick={onBack}>
                        Back
                    </BackButton>
                    <NextButton type="button" onClick={handleNextClick}>
                        Next
                    </NextButton>
                </DescribeFormButtons>
            )}
        </DescribeFormContainer>
    )
}

export default DescribeForm;
